import { gql, useQuery } from "@apollo/client";
import Session from "@lib/session";
import { useContext, useEffect, useRef } from "react";
import debugContext from "@lib/contexts/debug";
import FrameConfig from "@lib/classes/FrameConfig";
import { Content, QueryContentArgs } from "@graphql/api";

const query = (fields: (keyof Content)[]) => gql`
query content($charterProfile: Int, $groupProfile: Int, $serviceArea: Int, $showToken: Boolean, $tokenGroup: String) {
  ${contentSchema(fields)}
}
`;

const variables = (showToken?: boolean, defaults = {}) => {
  return {
    charterProfile: Session.getBooking().profiles.charter,
    groupProfile: Session.getBooking().profiles.group || Session.getBooking().profiles.coupon,
    serviceArea: defaults.serviceArea || Session.getBooking().serviceArea,
    showToken: showToken,
    tokenGroup: new FrameConfig().tokenGroup,
  };
};

export const contentSchema = (fields: (keyof Content)[]) => `
  content(charterProfile: $charterProfile, groupProfile: $groupProfile, serviceArea: $serviceArea, showToken: $showToken, tokenGroup: $tokenGroup) {
    id
    ${fields.join("\n")}
  }
`;

export async function getContent<T extends (keyof Content)[]>(
  fields: T = [],
  serviceArea?: number,
) {
  type Fields = (typeof fields)[number];
  return await global.window.apolloClient.query<ContentQuery<Fields>, QueryContentArgs>({
    query: query(fields),
    variables: variables(window.debugContent, { serviceArea: serviceArea }),
  });
}

interface ContentQuery<T extends keyof Content> {
  __typename?: "Query";
  content: Pick<Content, T>;
}

function useContent<T extends (keyof Content)[]>(fields: T = []) {
  const { debugMode } = useContext(debugContext);

  const queryVars: QueryContentArgs = variables(debugMode);

  type Fields = (typeof fields)[number];
  return useQuery<ContentQuery<Fields>, QueryContentArgs>(query(fields), { variables: queryVars });
}

export default useContent;
